import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Alert from './../../Alert/Alert'
import { LoadingSpinner } from '../../LoadingSpinner/LoadingSpinner'
import { useAuth } from '../../../../hooks/useAuth'
import { useLazyGetTenantQuery } from '../../../../redux/api/customerApi'
import { useLazyPostSupportRequestQuery } from '../../../../redux/api/notificationsApi'
import { useContractSupport } from '../../../../hooks/useContractSupport'

interface Props {
  onClose: () => void
}
interface SelectorOptions {
  [key: string]: string
}
interface IForm {
  selectedOption: string
  text: string
}
enum SelectorKeys {
  login = 'login',
  missingData = 'missingData',
  incorrectData = 'incorrectData',
  miscellaneous = 'miscellaneous',
  contractSupport = 'contractSupport',
}
export default function HelpComponentFunctional({ onClose }: Props) {
  const { t } = useTranslation('common')
  const user = useAuth()
  const tenantId = user.user?.tenant
  const [getTenant, tenant] = useLazyGetTenantQuery()
  const [postRequest] = useLazyPostSupportRequestQuery()
  const [form, setForm] = useState<IForm>({
    selectedOption: '',
    text: '',
  })
  const [reference, setReference] = useState<string>('')
  const [placeholder, setPlaceholder] = useState<string>()

  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [loading, setLoading] = useState(false)

  const characterLimit = 500
  const leftSymbols = characterLimit - form.text.length

  const contractRefEnabled = form.selectedOption && form.selectedOption !== SelectorKeys.login
  const contractRefRequired = form.selectedOption === SelectorKeys.contractSupport

  const formHasData = !!form.selectedOption && !!form.text
  const isFormValid = formHasData && (!!reference || !contractRefRequired)

  const selectorOptions: SelectorOptions = useMemo(() => {
    return {
      [SelectorKeys.login]: t('SUPPORT_LOGIN'),
      [SelectorKeys.missingData]: t('SUPPORT_MISSING_CONTRACTS'),
      [SelectorKeys.incorrectData]: t('SUPPORT_WRONG_INFORMATION'),
      [SelectorKeys.miscellaneous]: t('SUPPORT_MISCELLANEOUS'),
      [SelectorKeys.contractSupport]: t('SUPPORT_CONTRACT'),
    }
  }, [t])

  const { contractSupportRef } = useContractSupport()

  useEffect(() => {
    if (contractSupportRef) {
      const reference = Array.isArray(contractSupportRef) ? contractSupportRef[0] : contractSupportRef
      const placeholder = Array.isArray(contractSupportRef) ? contractSupportRef[1] : t('SUPPORT_ENTER_TEXT')
      setReference(reference)
      setPlaceholder(placeholder)
      setForm((prev) => ({ ...prev, selectedOption: SelectorKeys.contractSupport }))
    }
  }, [contractSupportRef, t])

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, selectedOption: e.target.value }))
  }
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, text: e.target.value }))
  }

  const handleAlertClose = () => {
    setShowAlert(false)
    setLoading(false)
    onClose()
  }

  const submitSupportRequest = async (tenantName?: string) => {
    if (!isFormValid) return
    setLoading(true)
    try {
      const formLabel = selectorOptions[form.selectedOption]

      const subject = [user.user?.displayName, tenantName && tenantName, formLabel].filter(Boolean).join(' - ')

      const body = [
        t('SUPPORT_REQUEST'),
        formLabel,
        user.user?.email,
        tenantName ?? '',
        contractRefEnabled && reference ? `Contract reference: ${reference}` : '',
        form.text,
      ]
        .filter(Boolean)
        .join(' \r\n ')

      await postRequest({
        subject,
        body,
      })

      setAlertMessage(t('SUPPORT_SUBMIT_SUCCESS_MESSAGE'))
      setShowAlert(true)
    } catch (error) {
      console.error(error)
      setAlertMessage(t('SUPPORT_SUBMIT_FAIL_MESSAGE'))
      setShowAlert(true)
    }
  }

  const handleSubmit = async () => {
    if (!tenantId) {
      await submitSupportRequest()
      return
    }

    getTenant(tenantId)
    if (tenant.data?.name) {
      submitSupportRequest(tenant.data?.name)
    } else submitSupportRequest()
    return
  }
  return (
    <div className="helpMenuArea">
      <div className="selectCategory">
        <label>{t('SUPPORT_CATEGORY')}</label>
        <div className="selectArea">
          <select name="select" value={form.selectedOption} onChange={handleSelectChange}>
            <option value="" disabled>
              {t('SUPPORT_CHOOSE_AMONG_THE_FOLLOWING')}
            </option>
            {Object.entries(selectorOptions)
              .sort(([, labelA], [, labelB]) => labelA.localeCompare(labelB))
              .map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
          </select>
          <i className="icon-bottom" />
        </div>
      </div>

      {contractRefEnabled && (
        <div className="messageBox">
          <label>{t('CONTRACT_REFERENCE')}</label>
          <textarea
            placeholder={t('SUPPORT_ENTER_REF_NUMBER')}
            name="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            style={{ height: '32px', minHeight: '32px' }}
          />
        </div>
      )}

      <div className="messageBox">
        <label>{t('SUPPORT_MESSAGE')}</label>
        <textarea
          placeholder={placeholder ?? t('SUPPORT_ENTER_TEXT')}
          name="text"
          value={form.text}
          onChange={handleTextChange}
          maxLength={characterLimit}
        />
        <div className="textSmall">{leftSymbols + ' ' + t('SUPPORT_SYMBOLS_LEFT')}</div>
      </div>

      <div className="btnArea">
        <button onClick={onClose} className="btn">
          {t('CANCEL')}
        </button>
        <button className="btn" onClick={handleSubmit} disabled={loading || !isFormValid}>
          {loading ? <LoadingSpinner size={14} /> : t('SUPPORT_SUBMIT')}
        </button>
      </div>

      <Alert title={t('INFO')} message={alertMessage} visible={showAlert} onClose={handleAlertClose} />
    </div>
  )
}
